import { useEffect, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { Search, FileText, AlertCircle, SlidersHorizontal, ChevronDown, X } from 'lucide-react'
import { useArticleSearch } from '../api/hooks/useArticles'
import type { ArticleSearchFilters } from '../api/services/articleService'
import type { WireArticle } from '../api/types'
import { useTopics, useRegions } from '../hooks/useTaxonomy'
import { TaxonomyPicker } from '../components/common/TaxonomyPicker'

// ─── Classification badge ─────────────────────────────────────────────────────

const CLASSIFICATION_COLORS: Record<string, string> = {
  U:  'border-jade-7  text-jade-11',
  C:  'border-blue-7  text-blue-11',
  S:  'border-amber-7 text-amber-11',
  TS: 'border-red-7   text-red-11',
}

function ClassificationBadge({ classification }: { classification?: string }) {
  if (!classification) return null
  const level = classification.split('//')[0].trim().toUpperCase()
  const colors = CLASSIFICATION_COLORS[level] ?? 'border-line text-fg-secondary'
  return (
    <span className={`type-badge border rounded px-1.5 py-0.5 ${colors}`}>
      {classification}
    </span>
  )
}

// ─── Article result card ──────────────────────────────────────────────────────

function ArticleResultCard({ article }: { article: WireArticle }) {
  const href = `/wire/articles/${article.articleId ?? article.id}`

  const date = article.publishedAt ?? article.updatedAt
  const dateStr = date
    ? new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null

  const blurb = article.subtitle ?? article.description ?? article.summary

  const thumbnailUrl = article.thumbnail?.presignedUrl
  const thumbnailAlt = article.thumbnail?.altText ?? ''
  const focalPoint   = article.thumbnail?.focalPoint

  return (
    <Link to={href} className="block group">
      <article className="relative overflow-hidden rounded-sm border border-line bg-surface shadow-sm transition-all duration-150 hover:shadow-md hover:border-line-strong">
        {/* Left accent strip */}
        <div className="absolute inset-y-0 left-0 w-[3px] bg-violet-9" />

        <div className="flex items-stretch">
          {/* Content */}
          <div className="min-w-0 flex-1 pl-4 pr-4 pt-3 pb-3">
            {/* Top meta row */}
            <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
              <ClassificationBadge classification={article.classification} />
              {article.productType !== 'standard' && (
                <span className="type-metadata text-fg-secondary uppercase">
                  {article.productType}
                </span>
              )}
              {dateStr && (
                <>
                  <span className="text-fg-secondary opacity-30 select-none">·</span>
                  <span className="type-metadata text-fg-secondary">{dateStr}</span>
                </>
              )}
              {article.documentId && (
                <>
                  <span className="text-fg-secondary opacity-30 select-none">·</span>
                  <span className="type-metadata text-fg-secondary font-mono">{article.documentId}</span>
                </>
              )}
            </div>

            {/* Title */}
            <h2 className="font-zodiak text-lg font-bold leading-snug text-fg transition-colors group-hover:text-violet-11 mb-1">
              {article.title}
            </h2>

            {/* Blurb */}
            {blurb && (
              <p className="text-sm text-fg-secondary leading-snug line-clamp-2 mb-2">
                {blurb}
              </p>
            )}

            {/* Topic + region pills */}
            {((article.topics?.length ?? 0) > 0 || (article.regions?.length ?? 0) > 0) && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {article.topics?.map((t) => (
                  <span
                    key={t}
                    className="type-badge rounded-full border border-violet-6 text-violet-11 px-1.5 py-0.5"
                  >
                    {t}
                  </span>
                ))}
                {article.regions?.map((r) => (
                  <span
                    key={r}
                    className="type-badge rounded-full border border-line text-fg-secondary px-1.5 py-0.5"
                  >
                    {r}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Thumbnail */}
          {thumbnailUrl && (
            <div className="ml-3 shrink-0 w-32 self-stretch">
              <img
                src={thumbnailUrl}
                alt={thumbnailAlt}
                className="h-full w-full object-cover"
                style={focalPoint
                  ? { objectPosition: `${focalPoint.x * 100}% ${focalPoint.y * 100}%` }
                  : undefined}
                loading="lazy"
              />
            </div>
          )}
        </div>
      </article>
    </Link>
  )
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="animate-pulse rounded-sm border border-line bg-surface">
      <div className="flex items-stretch">
        <div className="flex-1 pl-4 pr-4 pt-3 pb-3 space-y-2">
          <div className="flex gap-2">
            <div className="h-3 w-5 rounded bg-fill" />
            <div className="h-3 w-16 rounded bg-fill" />
            <div className="h-3 w-24 rounded bg-fill" />
          </div>
          <div className="h-5 w-3/4 rounded bg-fill" />
          <div className="h-3.5 w-1/2 rounded bg-fill" />
        </div>
        <div className="ml-3 shrink-0 w-32 h-24 bg-fill" />
      </div>
    </div>
  )
}

// ─── Filter URL helpers ───────────────────────────────────────────────────────

function listParam(p: URLSearchParams, key: string): string[] {
  const v = p.get(key)
  return v ? v.split(',').filter(Boolean) : []
}

function buildFilters(p: URLSearchParams): ArticleSearchFilters {
  const from = p.get('from')
  const to   = p.get('to')
  const rgn  = listParam(p, 'rgn')
  const tpc  = listParam(p, 'tpc')
  const pt   = listParam(p, 'pt')
  const po   = listParam(p, 'po')
  return {
    ...(from && { fromDate: `${from}T00:00:00Z` }),
    ...(to   && { toDate:   `${to}T23:59:59Z`   }),
    ...(rgn.length && { regionsAndCountries: rgn }),
    ...(tpc.length && { issuesAndTopics: tpc }),
    ...(pt.length  && { reportingAndProductTypes: pt }),
    ...(po.length  && { producingOffices: po }),
  }
}

type FilterDraft = {
  fromDate: string
  toDate: string
  regions: string[]
  topics: string[]
  productTypes: string[]
  offices: string[]
}

function draftFromUrl(p: URLSearchParams): FilterDraft {
  return {
    fromDate:        p.get('from') ?? '',
    toDate:          p.get('to')   ?? '',
    regions:         listParam(p, 'rgn'),
    topics:          listParam(p, 'tpc'),
    productTypes:    listParam(p, 'pt'),
    offices:         listParam(p, 'po'),
  }
}

function countActiveFilters(d: FilterDraft): number {
  return [
    d.fromDate || d.toDate,
    d.regions.length > 0,
    d.topics.length > 0,
    d.productTypes.length > 0,
    d.offices.length > 0,
  ].filter(Boolean).length
}

// ─── Product-type chip options ──────────────────────────────────────────────────

const PRODUCT_TYPE_OPTIONS = [
  { value: 'standard',   label: 'Standard'   },
  { value: 'biographic', label: 'Biographic' },
]

// ─── TagInput (freeform, used for Producing Office) ───────────────────────────

function TagInput({
  placeholder,
  values,
  onChange,
}: {
  placeholder: string
  values: string[]
  onChange: (v: string[]) => void
}) {
  const [text, setText] = useState('')

  function commit() {
    const trimmed = text.trim()
    if (trimmed && !values.includes(trimmed)) onChange([...values, trimmed])
    setText('')
  }

  return (
    <div className="flex flex-wrap items-center gap-1 rounded border border-line bg-canvas px-2 py-1.5 min-h-[34px] focus-within:ring-1 focus-within:ring-solid focus-within:border-solid">
      {values.map((v) => (
        <span key={v} className="flex items-center gap-0.5 rounded-full bg-fill border border-line-subtle px-2 py-0.5 text-xs text-fg">
          {v}
          <button
            type="button"
            onClick={() => onChange(values.filter((x) => x !== v))}
            className="ml-0.5 text-fg-secondary hover:text-fg leading-none"
            aria-label={`Remove ${v}`}
          >
            <X size={10} />
          </button>
        </span>
      ))}
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit() }
          if (e.key === 'Backspace' && !text && values.length) onChange(values.slice(0, -1))
        }}
        onBlur={commit}
        placeholder={values.length === 0 ? placeholder : ''}
        className="min-w-[120px] flex-1 bg-transparent text-sm text-fg placeholder:text-fg-secondary outline-none"
      />
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ArticleSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const queryFromUrl    = searchParams.get('q') ?? ''
  const filtersFromUrl  = buildFilters(searchParams)

  const { data: topicOptions = [], isLoading: topicsLoading } = useTopics()
  const { data: regionOptions = [], isLoading: regionsLoading } = useRegions()

  const [inputValue,   setInputValue]   = useState(queryFromUrl)
  const [filterDraft,  setFilterDraft]  = useState<FilterDraft>(() => draftFromUrl(searchParams))
  const [filtersOpen,  setFiltersOpen]  = useState(() => countActiveFilters(draftFromUrl(searchParams)) > 0)

  // Sync local form state when URL changes (back/forward navigation)
  useEffect(() => {
    const draft = draftFromUrl(searchParams)
    setInputValue(searchParams.get('q') ?? '')
    setFilterDraft(draft)
  }, [searchParams])

  const { data: results = [], isPending, isFetching, isError, error } =
    useArticleSearch(queryFromUrl, filtersFromUrl)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const q = inputValue.trim()
    const next: Record<string, string> = {}
    if (q)                                       next.q    = q
    if (filterDraft.fromDate)                    next.from = filterDraft.fromDate
    if (filterDraft.toDate)                      next.to   = filterDraft.toDate
    if (filterDraft.regions.length)              next.rgn  = filterDraft.regions.join(',')
    if (filterDraft.topics.length)               next.tpc  = filterDraft.topics.join(',')
    if (filterDraft.productTypes.length)         next.pt   = filterDraft.productTypes.join(',')
    if (filterDraft.offices.length)              next.po   = filterDraft.offices.join(',')
    if (Object.keys(next).length === 0) return
    setSearchParams(next)
  }

  function clearFilters() {
    const draft: FilterDraft = { fromDate: '', toDate: '', regions: [], topics: [], productTypes: [], offices: [] }
    setFilterDraft(draft)
    setSearchParams(queryFromUrl ? { q: queryFromUrl } : {})
  }

  function toggleChip<T extends string>(arr: T[], value: T): T[] {
    return arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value]
  }

  const hasQuery        = queryFromUrl.length >= 2
  const isLoading       = hasQuery && (isPending || isFetching)
  const showEmpty       = hasQuery && !isLoading && !isError && results.length === 0
  const activeCount     = countActiveFilters(filterDraft)

  return (
    <div className="relative min-h-screen">
      <div className="relative mx-auto max-w-5xl px-6 pt-10 pb-16">
        {/* Page header */}
        <div className="border-b border-line pb-6 mb-8">
          <p className="type-metadata text-fg-secondary mb-1"><span className="text-violet-11">WIRe</span> · Articles</p>
          <h1 className="font-zodiak text-4xl font-bold text-fg mb-5">
            {queryFromUrl ? (
              <>
                Results for{' '}
                <span className="text-violet-11">"{queryFromUrl}"</span>
              </>
            ) : (
              'Search'
            )}
          </h1>

          {/* Search form */}
          <form onSubmit={handleSubmit}>
            <div className="flex gap-2 mb-3">
              <div className="relative flex-1">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-secondary pointer-events-none"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Search articles…"
                  className="h-9 w-full rounded border border-line bg-surface pl-8 pr-3 text-sm text-fg placeholder:text-fg-secondary outline-none focus:ring-1 focus:ring-solid focus:border-solid"
                  autoFocus
                />
              </div>
              <button
                type="submit"
                className="h-9 rounded bg-solid px-4 text-xs font-bold uppercase tracking-widest text-white hover:bg-solid-hover transition-colors"
              >
                Search
              </button>
            </div>

            {/* Advanced Search toggle */}
            <button
              type="button"
              onClick={() => setFiltersOpen((o) => !o)}
              className="flex items-center gap-1.5 text-sm font-semibold text-fg-secondary hover:text-fg transition-colors"
            >
              <SlidersHorizontal size={14} aria-hidden="true" />
              Advanced Search
              {activeCount > 0 && (
                <span className="rounded-full bg-violet-9 px-1.5 py-0.5 text-[9px] font-bold text-white leading-none">
                  {activeCount}
                </span>
              )}
              <ChevronDown
                size={12}
                aria-hidden="true"
                className={`transition-transform duration-200 ${filtersOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Advanced Search panel */}
            {filtersOpen && (
              <div className="mt-3 rounded border border-line bg-surface/60 p-5 space-y-6">

                {/* Date range */}
                <div>
                  <p className="type-section-label text-fg-secondary mb-2">Date Range</p>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="flex flex-col gap-1">
                      <span className="text-xs text-fg-secondary">From</span>
                      <input
                        type="date"
                        value={filterDraft.fromDate}
                        onChange={(e) => setFilterDraft((d) => ({ ...d, fromDate: e.target.value }))}
                        className="h-8 rounded border border-line bg-canvas px-2 text-sm text-fg outline-none focus:ring-1 focus:ring-solid focus:border-solid [color-scheme:light] dark:[color-scheme:dark]"
                      />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-xs text-fg-secondary">To</span>
                      <input
                        type="date"
                        value={filterDraft.toDate}
                        onChange={(e) => setFilterDraft((d) => ({ ...d, toDate: e.target.value }))}
                        className="h-8 rounded border border-line bg-canvas px-2 text-sm text-fg outline-none focus:ring-1 focus:ring-solid focus:border-solid [color-scheme:light] dark:[color-scheme:dark]"
                      />
                    </label>
                  </div>
                </div>

                {/* Topics */}
                <TaxonomyPicker
                  label="Issues & Topics"
                  options={topicOptions}
                  selected={filterDraft.topics}
                  onChange={(v) => setFilterDraft((d) => ({ ...d, topics: v }))}
                  placeholder="No topics selected"
                  loading={topicsLoading}
                />

                {/* Regions */}
                <TaxonomyPicker
                  label="Regions & Countries"
                  options={regionOptions}
                  selected={filterDraft.regions}
                  onChange={(v) => setFilterDraft((d) => ({ ...d, regions: v }))}
                  placeholder="No regions selected"
                  loading={regionsLoading}
                />

                {/* Producing Office */}
                <div>
                  <p className="type-section-label text-fg-secondary mb-2">Producing Office</p>
                  <TagInput
                    placeholder="Type an office and press Enter…"
                    values={filterDraft.offices}
                    onChange={(v) => setFilterDraft((d) => ({ ...d, offices: v }))}
                  />
                </div>

                {/* Product Type */}
                <div>
                  <p className="type-section-label text-fg-secondary mb-2">Product Type</p>
                  <div className="flex flex-wrap gap-2">
                    {PRODUCT_TYPE_OPTIONS.map(({ value, label }) => {
                      const active = filterDraft.productTypes.includes(value)
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setFilterDraft((d) => ({ ...d, productTypes: toggleChip(d.productTypes, value) }))}
                          className={`type-badge rounded border px-2 py-1 transition-colors ${
                            active
                              ? 'bg-violet-3 border-violet-7 text-violet-11'
                              : 'border-line text-fg-secondary hover:border-line-strong hover:text-fg'
                          }`}
                        >
                          {label}
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Clear */}
                {activeCount > 0 && (
                  <div className="flex justify-end pt-1">
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="flex items-center gap-1 text-xs text-fg-secondary hover:text-fg transition-colors"
                    >
                      <X size={11} aria-hidden="true" />
                      Clear all filters
                    </button>
                  </div>
                )}
              </div>
            )}
          </form>
        </div>

        {/* Result count */}
        {hasQuery && !isLoading && !isError && results.length > 0 && (
          <p className="type-metadata text-fg-secondary mb-4">
            {results.length} result{results.length === 1 ? '' : 's'}
          </p>
        )}

        {/* Error state */}
        {isError && (
          <div className="flex items-center gap-2 rounded border border-red-7 bg-red-3 px-4 py-3 text-sm text-red-11">
            <AlertCircle size={14} className="shrink-0" aria-hidden="true" />
            {error instanceof Error ? error.message : 'Search failed. Please try again.'}
          </div>
        )}

        {/* Skeleton */}
        {isLoading && (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}

        {/* Results list */}
        {!isLoading && results.length > 0 && (
          <div className="space-y-3">
            <p className="text-xs text-fg-secondary">
              {results.length} {results.length === 1 ? 'result' : 'results'}
            </p>
            {results.map((article) => (
              <ArticleResultCard key={article.id} article={article} />
            ))}
          </div>
        )}

        {/* Empty state */}
        {showEmpty && (
          <div className="flex flex-col items-center py-16 text-center">
            <FileText size={32} className="mb-3 text-fg-secondary opacity-30" aria-hidden="true" />
            <p className="font-zodiak text-lg font-semibold text-fg-secondary opacity-60">
              No results for "{queryFromUrl}"
            </p>
            <p className="mt-1 text-sm text-fg-secondary opacity-60">
              Try a different search term or broaden your query
            </p>
          </div>
        )}

        {/* No query yet */}
        {!hasQuery && (
          <div className="flex flex-col items-center py-16 text-center">
            <Search size={32} className="mb-3 text-fg-secondary opacity-30" aria-hidden="true" />
            <p className="font-zodiak text-lg font-semibold text-fg-secondary opacity-60">
              Enter a search term above
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
